/**
 * Toast Notification System
 *
 * Converts ALL WooCommerce notices to toast notifications
 */

(function() {
    'use strict';

    // Toast container
    let toastContainer = null;
    let isInitialized = false;

    /**
     * Initialize toast container
     */
    function initToastContainer() {
        if (!toastContainer) {
            toastContainer = document.createElement('div');
            toastContainer.className = 'toast-container';
            document.body.appendChild(toastContainer);
            document.body.classList.add('woocommerce-toasts-enabled');
        }
        return toastContainer;
    }

    /**
     * Get icon for toast type
     */
    function getToastIcon(type) {
        const icons = {
            success: '✓',
            error: '✕',
            info: 'ℹ',
            warning: '⚠'
        };
        return icons[type] || icons.info;
    }

    /**
     * Show toast notification
     */
    function showToast(message, type = 'info', duration = 5000) {
        const container = initToastContainer();

        // Create toast element
        const toast = document.createElement('div');
        toast.className = `toast toast--${type}`;

        // Create icon
        const icon = document.createElement('div');
        icon.className = 'toast__icon';
        icon.textContent = getToastIcon(type);
        icon.setAttribute('aria-hidden', 'true');

        // Create content
        const content = document.createElement('div');
        content.className = 'toast__content';

        // Parse message (handle HTML)
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = message;
        const paragraphs = tempDiv.querySelectorAll('p');

        if (paragraphs.length > 0) {
            paragraphs.forEach(p => {
                const pClone = p.cloneNode(true);
                content.appendChild(pClone);
            });
        } else {
            content.innerHTML = message;
        }

        // Create close button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'toast__close';
        closeBtn.setAttribute('aria-label', 'Close notification');
        closeBtn.setAttribute('type', 'button');
        closeBtn.addEventListener('click', () => {
            hideToast(toast);
        });

        // Assemble toast
        toast.appendChild(icon);
        toast.appendChild(content);
        toast.appendChild(closeBtn);

        // Add to container
        container.appendChild(toast);

        // Trigger animation
        requestAnimationFrame(() => {
            toast.classList.add('toast--show');
        });

        // Auto-close if duration is set
        if (duration > 0) {
            setTimeout(() => {
                hideToast(toast);
            }, duration);
        }

        return toast;
    }

    /**
     * Hide toast notification
     */
    function hideToast(toast) {
        if (!toast || !toast.parentNode) {
            return;
        }

        toast.classList.remove('toast--show');
        toast.classList.add('toast--hide');

        setTimeout(() => {
            if (toast.parentNode) {
                toast.parentNode.removeChild(toast);
            }
        }, 300);
    }

    /**
     * Convert WooCommerce notices to toasts
     */
    function convertWooCommerceNotices() {
        // Find ALL WooCommerce notices - use more comprehensive selectors
        const selectors = [
            '.woocommerce-message',
            '.woocommerce-error',
            '.woocommerce-info',
            '.woocommerce-notice',
            'div.woocommerce-message',
            'div.woocommerce-error',
            'div.woocommerce-info',
            'ul.woocommerce-error',
            'ul.woocommerce-info',
            '.woocommerce .woocommerce-message',
            '.woocommerce .woocommerce-error',
            '.woocommerce .woocommerce-info'
        ];

        let allNotices = [];
        selectors.forEach(selector => {
            try {
                const notices = document.querySelectorAll(selector);
                notices.forEach(n => allNotices.push(n));
            } catch(e) {
                // Invalid selector, skip
            }
        });

        // Remove duplicates
        const uniqueNotices = Array.from(new Set(allNotices));

        uniqueNotices.forEach(notice => {
            // Skip if already processed
            if (notice.dataset.toastProcessed === 'true') {
                return;
            }

            // Skip if already hidden by our system
            if (notice.style.display === 'none' && notice.dataset.toastProcessed === 'true') {
                return;
            }

            // Mark as processed
            notice.dataset.toastProcessed = 'true';

            // Determine notice type
            let type = 'info';
            if (notice.classList.contains('woocommerce-message')) {
                type = 'success';
            } else if (notice.classList.contains('woocommerce-error')) {
                type = 'error';
            } else if (notice.classList.contains('woocommerce-info')) {
                type = 'info';
            }

            // Get message content
            let message = notice.innerHTML.trim();

            // Remove close buttons and dismiss links
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = message;
            const elementsToRemove = tempDiv.querySelectorAll(
                '.woocommerce-message-close, .notice-dismiss, a[href*="remove_notice"], button[class*="close"], .close'
            );
            elementsToRemove.forEach(el => el.remove());
            message = tempDiv.innerHTML.trim();

            // Show toast if message exists
            if (message && message.length > 0) {
                showToast(message, type, 5000);
            }

            // Hide original notice completely
            notice.style.display = 'none';
            notice.style.visibility = 'hidden';
            notice.style.position = 'absolute';
            notice.style.left = '-9999px';
        });
    }

    /**
     * Initialize toast system
     */
    function init() {
        if (isInitialized) {
            return;
        }
        isInitialized = true;

        // Initialize container immediately
        initToastContainer();

        // Convert existing notices immediately
        convertWooCommerceNotices();

        // Convert again after DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', function() {
                convertWooCommerceNotices();
                setTimeout(convertWooCommerceNotices, 100);
                setTimeout(convertWooCommerceNotices, 500);
            });
        } else {
            setTimeout(convertWooCommerceNotices, 100);
            setTimeout(convertWooCommerceNotices, 500);
        }

        // Watch for dynamically added notices
        const observer = new MutationObserver(function(mutations) {
            let shouldConvert = false;

            mutations.forEach(function(mutation) {
                mutation.addedNodes.forEach(function(node) {
                    if (node.nodeType === 1) {
                        // Check if node itself is a notice
                        if (node.classList && (
                            node.classList.contains('woocommerce-message') ||
                            node.classList.contains('woocommerce-error') ||
                            node.classList.contains('woocommerce-info') ||
                            node.classList.contains('woocommerce-notice')
                        )) {
                            shouldConvert = true;
                        }

                        // Check children
                        if (node.querySelectorAll) {
                            const childNotices = node.querySelectorAll(
                                '.woocommerce-message, .woocommerce-error, .woocommerce-info, .woocommerce-notice'
                            );
                            if (childNotices.length > 0) {
                                shouldConvert = true;
                            }
                        }

                        // Check for WooCommerce notice containers
                        if (node.classList && node.classList.contains('woocommerce-notices-wrapper')) {
                            shouldConvert = true;
                        }
                    }
                });
            });

            if (shouldConvert) {
                setTimeout(convertWooCommerceNotices, 50);
                setTimeout(convertWooCommerceNotices, 200);
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false
        });

        // Listen for WooCommerce events (jQuery)
        if (typeof jQuery !== 'undefined') {
            // Add to cart event
            jQuery(document.body).on('added_to_cart', function(event, fragments, cart_hash, $button) {
                setTimeout(convertWooCommerceNotices, 100);
                setTimeout(convertWooCommerceNotices, 500);
            });

            // Fragment refresh events
            jQuery(document.body).on('wc_fragments_refreshed wc_fragments_loaded updated_wc_div', function() {
                setTimeout(convertWooCommerceNotices, 100);
                setTimeout(convertWooCommerceNotices, 500);
            });

            // Checkout events
            jQuery(document.body).on('updated_checkout updated_cart_totals', function() {
                setTimeout(convertWooCommerceNotices, 100);
                setTimeout(convertWooCommerceNotices, 500);
            });

            // Hook into AJAX complete
            jQuery(document).ajaxComplete(function(event, xhr, settings) {
                if (settings.url && (
                    settings.url.indexOf('wc-ajax') !== -1 ||
                    settings.url.indexOf('add_to_cart') !== -1 ||
                    settings.url.indexOf('update_cart') !== -1 ||
                    settings.url.indexOf('checkout') !== -1
                )) {
                    setTimeout(convertWooCommerceNotices, 200);
                    setTimeout(convertWooCommerceNotices, 600);
                }
            });

            // Override WooCommerce's fragment handler if possible
            jQuery(document.body).on('wc_fragment_refresh', function() {
                setTimeout(convertWooCommerceNotices, 100);
                setTimeout(convertWooCommerceNotices, 500);
            });
        }

        // Listen for vanilla JS events
        document.body.addEventListener('added_to_cart', function() {
            setTimeout(convertWooCommerceNotices, 100);
            setTimeout(convertWooCommerceNotices, 500);
        });

        document.body.addEventListener('updated_checkout', function() {
            setTimeout(convertWooCommerceNotices, 100);
            setTimeout(convertWooCommerceNotices, 500);
        });

        document.body.addEventListener('updated_cart_totals', function() {
            setTimeout(convertWooCommerceNotices, 100);
            setTimeout(convertWooCommerceNotices, 500);
        });

        // Periodic check as ultimate fallback - more aggressive
        setInterval(function() {
            const notices = document.querySelectorAll(
                '.woocommerce-message, .woocommerce-error, .woocommerce-info, .woocommerce-notice, div.woocommerce-message, div.woocommerce-error, ul.woocommerce-error'
            );
            const unprocessed = Array.from(notices).filter(n => {
                const isProcessed = n.dataset.toastProcessed === 'true';
                const isHidden = window.getComputedStyle(n).display === 'none' ||
                                window.getComputedStyle(n).visibility === 'hidden';
                return !isProcessed && !isHidden;
            });
            if (unprocessed.length > 0) {
                convertWooCommerceNotices();
            }
        }, 300); // Check every 300ms - more frequent
    }

    // Start immediately - run as early as possible
    (function() {
        if (document.body) {
            init();
        } else {
            // Wait for body if not ready
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', init);
            } else {
                // Use immediate function to run as soon as possible
                if (window.requestAnimationFrame) {
                    requestAnimationFrame(init);
                } else {
                    setTimeout(init, 0);
                }
            }
        }
    })();

    // Expose API
    window.showToast = showToast;
    window.hideToast = hideToast;
    window.convertWooCommerceNotices = convertWooCommerceNotices;

})();
